import { CellProps } from 'react-table'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import {
  PlainHeader,
  sortableColumn,
  StackedCell,
  StatusDot,
  StatusDotTone,
  TableColumn,
} from '../../../../../../components'
import { NotaList } from '../../core/_models'
import { NotaActionsCell } from './NotaActionsCell'
import { SituacaoNota, situacaoNotaLabels } from '../../../../../../../_metronic/helpers'
import { TFunction } from '_metronic/i18n/Metronici18n'

/** Cor do ponto por situação da nota — também usada no resumo da tela de itens da nota. */
export const situacaoNotaTone: Record<SituacaoNota, StatusDotTone> = {
  [SituacaoNota.Analise]: 'warning',
  [SituacaoNota.Processamento]: 'info',
  [SituacaoNota.Aguardando_Nota]: 'warning',
  [SituacaoNota.Nota_Recebida]: 'primary',
  [SituacaoNota.Aguardando_Pagamento]: 'primary',
  [SituacaoNota.Paga]: 'success',
  [SituacaoNota.Cancelada]: 'danger',
}

/** Validação da nota enviada (antes era só um ponto colorido, sem texto). */
const validacao = (nota: NotaList): { label: string; tone: StatusDotTone } =>
  nota.analise
    ? { label: 'Em análise', tone: 'warning' }
    : nota.valida
    ? { label: 'Válida', tone: 'success' }
    : { label: 'Inválida', tone: 'danger' }

/**
 * @param isProfissional na visão do próprio profissional não aparecem valor e adicional (só o
 * total).
 */
export const notaColumns = (
  t: TFunction,
  isProfissional: boolean
): ReadonlyArray<TableColumn<NotaList>> => [
  // Coluna do botão de expandir (o ModernTable desenha o botão na coluna de id `expand`).
  {
    Header: () => <span className='visually-hidden'>Detalhes</span>,
    id: 'expand',
    cellWidth: '56px',
  },
  sortableColumn<NotaList>({
    id: 'profissional.nome',
    useQueryRequest,
    fields: [{ field: 'profissional.nome', label: t('cols.profissional') }],
    Cell: ({ row }: CellProps<NotaList>) => (
      <StackedCell primary={row.original.profissional?.nome} />
    ),
  }),
  sortableColumn<NotaList>({
    id: 'mesAno.mes',
    cellWidth: '120px',
    useQueryRequest,
    fields: [{ field: 'mesAno.mes', label: t('cols.periodo') }],
    Cell: ({ row }: CellProps<NotaList>) => {
      const { mesSigla, ano } = row.original.mesAno ?? {}
      return <StackedCell tabular primary={mesSigla && ano ? `${mesSigla}/${ano}` : undefined} />
    },
  }),
  // Valor com o adicional como apoio; os dois seguem ordenáveis (só na visão do financeiro).
  ...(isProfissional
    ? []
    : [
        sortableColumn<NotaList>({
          id: 'valor',
          cellWidth: '184px',
          align: 'end',
          hideOnMobile: true,
          useQueryRequest,
          fields: [
            { field: 'valor', label: t('cols.valor') },
            // "Adicional" (e não "Valor adicional"): o "Valor /" já está no cabeçalho.
            { field: 'valorAdicional', label: 'Adicional' },
          ],
          Cell: ({ row }: CellProps<NotaList>) => (
            <StackedCell
              tabular
              primary={row.original.valorFormatado}
              secondary={row.original.valorAdicionalFormatado}
            />
          ),
        }),
      ]),
  {
    Header: () => <PlainHeader>{t('cols.valor_total')}</PlainHeader>,
    id: 'valorTotal',
    cellWidth: '136px',
    align: 'end',
    Cell: ({ row }: CellProps<NotaList>) => (
      <StackedCell tabular primary={row.original.valorTotalFormatado} />
    ),
  },
  sortableColumn<NotaList>({
    id: 'situacao',
    cellWidth: '192px',
    useQueryRequest,
    fields: [{ field: 'situacao', label: t('cols.situacao') }],
    Cell: ({ row }: CellProps<NotaList>) => {
      const { situacao } = row.original
      return (
        <StatusDot
          label={situacao ? situacaoNotaLabels[situacao] : undefined}
          tone={situacao ? situacaoNotaTone[situacao] : undefined}
        />
      )
    },
  }),
  {
    Header: () => <PlainHeader>Validação</PlainHeader>,
    id: 'validacao',
    cellWidth: '128px',
    hideOnMobile: true,
    Cell: ({ row }: CellProps<NotaList>) => <StatusDot {...validacao(row.original)} />,
  },
  {
    Header: () => <PlainHeader>{t('cols.actions')}</PlainHeader>,
    id: 'actions',
    cellWidth: '104px',
    align: 'end',
    Cell: ({ row }: CellProps<NotaList>) => <NotaActionsCell nota={row.original} />,
  },
]
